import { useState } from 'react';
import type { ConnectionId, ConnectionType } from '@/types';
import { CONNECTION_TYPES } from '@/types';
import { useWorkspaceStore, selectActiveDocument } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import {
  deleteConnectionsCommand,
  reverseConnectionCommand,
  setConnectionLabelCommand,
  setConnectionTypeCommand,
  validateEdge,
} from '@/features/connections/connectionCommands';
import { isSymmetric } from '@/core/graph/connectionGraph';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import NativeSelect from '@mui/material/NativeSelect';
import styles from './Inspector.module.css';

const TYPE_LABELS: Readonly<Record<ConnectionType, string>> = {
  'linked-to': 'Linked to',
  'related-to': 'Related to',
  references: 'References',
  'depends-on': 'Depends on',
  blocks: 'Blocks',
  contains: 'Contains',
};

/** Inspector body for a single selected connection: relationship, direction, label, ends. */
export const ConnectionInspector = ({ id }: { id: ConnectionId }) => {
  const connection = useWorkspaceStore((s) => s.workspaces[s.activeWorkspaceId]?.connections[id]);
  const names = useWorkspaceStore((s) => {
    const doc = s.workspaces[s.activeWorkspaceId];
    const c = doc?.connections[id];
    if (!doc || !c) return '';
    return `${doc.objects[c.sourceId]?.name ?? '?'}|${doc.objects[c.targetId]?.name ?? '?'}`;
  });
  const dispatch = useWorkspaceStore((s) => s.dispatch);
  const [label, setLabel] = useState(connection?.label ?? '');
  const [seen, setSeen] = useState(connection?.label ?? '');
  if (!connection) return null;
  if (seen !== connection.label) {
    setSeen(connection.label);
    setLabel(connection.label);
  }
  const [sourceName = '?', targetName = '?'] = names.split('|');
  const run = (
    make: (doc: NonNullable<ReturnType<typeof selectActiveDocument>>) => ReturnType<typeof setConnectionTypeCommand>,
  ) => {
    const doc = selectActiveDocument(useWorkspaceStore.getState());
    const cmd = doc && make(doc);
    if (cmd) dispatch(cmd);
  };
  const doc = selectActiveDocument(useWorkspaceStore.getState());
  const blockedTypes = new Set(
    doc
      ? CONNECTION_TYPES.filter(
          (t) =>
            t !== connection.type && validateEdge(doc, connection.sourceId, connection.targetId, t, id) === 'cycle',
        )
      : [],
  );

  return (
    <>
      <header className={styles.header}>
        <span className={styles.kind}>Connection</span>
        <strong data-testid="inspector-connection-title">
          {sourceName} {isSymmetric(connection.type) ? '↔' : '→'} {targetName}
        </strong>
      </header>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Relationship</h3>
        <label className={styles.row}>
          <span className={styles.label}>Type</span>
          <NativeSelect
            className={styles.grow}
            disableUnderline
            value={connection.type}
            inputProps={
              { 'aria-label': 'Relationship type', 'data-testid': 'connection-type' } as Record<string, unknown>
            }
            onChange={(e) => {
              run((d) => setConnectionTypeCommand(d, id, e.target.value as ConnectionType));
            }}
          >
            {CONNECTION_TYPES.map((t) => (
              <option key={t} value={t} disabled={blockedTypes.has(t)}>
                {TYPE_LABELS[t]}
                {blockedTypes.has(t) ? ' (would cycle)' : ''}
              </option>
            ))}
          </NativeSelect>
        </label>
        <label className={styles.row}>
          <span className={styles.label}>Label</span>
          <InputBase
            className={styles.field}
            value={label}
            inputProps={{ 'aria-label': 'Label', 'data-testid': 'connection-label' }}
            onChange={(e) => {
              setLabel(e.target.value);
            }}
            onBlur={() => {
              if (label.trim() !== connection.label) run((d) => setConnectionLabelCommand(d, id, label));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              e.stopPropagation();
            }}
          />
        </label>
        <div className={styles.buttons}>
          <Button
            variant="outlined"
            color="inherit"
            className={styles.grow}
            data-testid="connection-reverse"
            onClick={() => {
              run((d) => reverseConnectionCommand(d, id));
            }}
          >
            {isSymmetric(connection.type) ? 'Swap ends' : 'Reverse'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            className={styles.grow}
            data-testid="connection-delete"
            onClick={() => {
              run((d) => deleteConnectionsCommand(d, [id]));
              useSelectionStore.getState().clear();
            }}
          >
            Delete
          </Button>
        </div>
      </section>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Ends</h3>
        <div className={styles.row}>
          <span className={styles.label}>From</span>
          <span className={styles.mono}>{sourceName}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>To</span>
          <span className={styles.mono}>{targetName}</span>
        </div>
        <p className={styles.hint}>Drag an end grip on the canvas to reconnect.</p>
      </section>
    </>
  );
};
